/*
 * /forms/$patientFormId — fill out or review an assigned form
 */
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Page,
	Placeholder,
	toast,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { CheckCircle, Clock, Download, FileText } from "lucide-react"
import { trpc } from "../../trpc"
import { getFormDefinition } from "../../features/forms/form-definitions"
import { FormRenderer } from "../../features/forms/form-renderer"

export const Route = createFileRoute("/forms/$patientFormId")({
	component: FormPage,
})

function FormPage() {
	const { patientFormId } = Route.useParams()
	const utils = trpc.useUtils()

	const { data, isLoading } = trpc.patientForm.getWithForm.useQuery({ id: patientFormId })
	const [responses, setResponses] = useState<Record<string, string>>({})
	const [initialized, setInitialized] = useState(false)

	// Populate responses from DB once loaded
	if (data && !initialized) {
		setResponses((data.patientForm.responses as Record<string, string>) ?? {})
		setInitialized(true)
	}

	const saveResponses = trpc.patientForm.saveResponses.useMutation({
		onSuccess: () => {
			utils.patientForm.getWithForm.invalidate()
			toast.success("Progress saved")
		},
		onError: (e) => toast.error(e.message),
	})

	const submit = trpc.patientForm.submit.useMutation({
		onSuccess: () => {
			utils.patientForm.getWithForm.invalidate()
			utils.patientForm.getAllByFilter.invalidate()
			toast.success("Form submitted")
			window.history.back()
		},
		onError: (e) => toast.error(e.message),
	})

	const reopen = trpc.patientForm.reopen.useMutation({
		onSuccess: () => {
			utils.patientForm.getWithForm.invalidate()
			toast.success("Form reopened for editing")
		},
		onError: (e) => toast.error(e.message),
	})

	const generatePdf = trpc.patientForm.generatePdf.useMutation({
		onSuccess: (result) => {
			window.open(result.downloadUrl, "_blank")
			toast.success("PDF generated and saved to attachments")
		},
		onError: (e) => toast.error(e.message),
	})

	if (isLoading) return <Page centerVertical><Placeholder title="Loading…" /></Page>
	if (!data) return <Page centerVertical><Placeholder title="Form not found" /></Page>

	const { patientForm, form } = data
	const definition = getFormDefinition(form.slug)
	if (!definition) return <Page centerVertical><Placeholder title="Unknown form type" /></Page>

	const isCompleted = patientForm.status === "completed"
	const isSaving = saveResponses.isPending
	const isSubmitting = submit.isPending

	function handleChange(key: string, value: string) {
		setResponses((prev) => ({ ...prev, [key]: value }))
	}

	const completedDate = patientForm.completedAt
		? new Date(patientForm.completedAt).toLocaleDateString("en-CA", {
				year: "numeric", month: "long", day: "numeric",
		  })
		: null

	// Calculate live score from current responses
	const liveScore = definition.scoring?.(responses)

	// Stored score from DB (only present after submit)
	const storedScore = patientForm.score

	function handleSubmit() {
		const scoreTotal = liveScore?.total
		submit.mutate({ id: patientFormId, responses, score: scoreTotal })
	}

	return (
		<Page centerVertical={false}>
			<div className="max-w-3xl mx-auto px-4 py-8 flex flex-col gap-6">
				{/* Header */}
				<div className="flex items-start justify-between gap-4">
					<div className="flex flex-col gap-1">
						<div className="flex items-center gap-2">
							<FileText className="w-5 h-5 text-teal-700" />
							<h1 className="text-xl font-bold">{form.name}</h1>
						</div>
						{form.description && (
							<p className="text-sm text-muted-foreground">{form.description}</p>
						)}
					</div>
					<div className="flex flex-col items-end gap-1">
						{isCompleted ? (
							<Badge className="bg-green-100 text-green-800 border-green-200 flex items-center gap-1">
								<CheckCircle className="w-3 h-3" />
								Completed
							</Badge>
						) : (
							<Badge variant="outline" className="flex items-center gap-1">
								<Clock className="w-3 h-3" />
								{patientForm.status === "in_progress" ? "In Progress" : "Not Started"}
							</Badge>
						)}
						{completedDate && (
							<span className="text-xs text-muted-foreground">{completedDate}</span>
						)}
					</div>
				</div>

				{/* Questions */}
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Questions</CardTitle>
					</CardHeader>
					<CardContent>
						<FormRenderer
							definition={definition}
							responses={responses}
							onChange={handleChange}
							readonly={isCompleted}
							hideScore
						/>
					</CardContent>
				</Card>

				{/* Results — always rendered for scoring forms to avoid layout shift */}
				{definition.scoring && (
					<Card className={isCompleted ? "border-teal-200 bg-teal-50/40" : ""}>
						<CardHeader>
							<CardTitle className="text-base flex items-center gap-2">
								<CheckCircle className={`w-4 h-4 ${isCompleted ? "text-teal-700" : "text-muted-foreground"}`} />
								Results
							</CardTitle>
						</CardHeader>
						<CardContent>
							{(() => {
								const score = isCompleted
									? (storedScore !== null && storedScore !== undefined
										? { total: storedScore, max: definition.scoring!(responses).max, interpretation: definition.scoring!(responses).interpretation }
										: definition.scoring!(responses))
									: liveScore ?? definition.scoring!(responses)
								return (
									<div className="flex flex-col gap-3">
										<div className="flex items-baseline gap-3">
											<span className="text-3xl font-bold text-teal-800">{score.total}</span>
											<span className="text-sm text-muted-foreground">/ {score.max}</span>
											{score.interpretation && (
												<span className="ml-1 text-sm font-semibold text-teal-700">{score.interpretation}</span>
											)}
										</div>
										{/* Score range guide */}
										{definition.scoreRanges && (
											<div className="flex flex-col gap-1 pt-1">
												{definition.scoreRanges.map((range) => {
													const active = score.total >= range.min && score.total <= range.max
													return (
														<div
															key={range.label}
															className={`flex items-center gap-2 text-sm font-semibold px-2 py-1 rounded ${active ? "bg-teal-100 text-teal-900" : "text-muted-foreground"}`}
														>
															<span className="w-14 text-right text-xs">{range.min}–{range.max}</span>
															<span>{range.label}</span>
															{active && <CheckCircle className="w-3 h-3 text-teal-600 ml-auto" />}
														</div>
													)
												})}
											</div>
										)}
									</div>
								)
							})()}
						</CardContent>
					</Card>
				)}

				{/* Actions */}
				<div className="flex gap-3 justify-end">
					<Button variant="outline" onClick={() => window.history.back()}>
						Back
					</Button>

					{isCompleted ? (
						<>
							{(form as any).templateS3Key && (
								<Button
									variant="outline"
									onClick={() => generatePdf.mutate({ id: patientFormId })}
									disabled={generatePdf.isPending}
									className="flex items-center gap-2"
								>
									<Download className="w-4 h-4" />
									{generatePdf.isPending ? "Generating…" : "Save & Download PDF"}
								</Button>
							)}
							<Button
								variant="outline"
								onClick={() => reopen.mutate({ id: patientFormId })}
								disabled={reopen.isPending}
							>
								{reopen.isPending ? "Reopening…" : "Edit Responses"}
							</Button>
						</>
					) : (
						<>
							<Button
								variant="outline"
								onClick={() => saveResponses.mutate({ id: patientFormId, responses })}
								disabled={isSaving || isSubmitting}
							>
								{isSaving ? "Saving…" : "Save Progress"}
							</Button>
							<Button
								onClick={handleSubmit}
								disabled={isSaving || isSubmitting}
								className="bg-teal-700 hover:bg-teal-800 text-white"
							>
								{isSubmitting ? "Submitting…" : "Submit Form"}
							</Button>
						</>
					)}
				</div>
			</div>
		</Page>
	)
}
